import { computed, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { t } from '@/locales'
import { useCountdown } from '@vueuse/core'

export const SCENE_TYPES = {
  DIAMOND_PAGE: 'diamond_page',
  VIP_PAGE: 'vip_page',
}

/**
 * 默认 scene 到路由的映射
 */
export const SCENE_ROUTE_MAP = {
  [SCENE_TYPES.DIAMOND_PAGE]: {
    path: '/diamond',
    name: t('nav.diamond'),
  },
  [SCENE_TYPES.VIP_PAGE]: {
    path: '/vip',
    name: t('nav.vip'),
  },
}

/**
 * 根据 scene 参数获取重定向路径
 * @param {string} scene - 场景标识
 * @param {Object} customSceneMap - 自定义 scene 映射（可选）
 * @returns {string} 重定向路径
 */
export function getRedirectPathByScene(scene, customSceneMap = {}) {
  const sceneRouteMap = {
    ...SCENE_ROUTE_MAP,
    ...customSceneMap,
  }
  return sceneRouteMap[scene]
}

/**
 * 支付重定向 Composable
 * @param {Object} options - 配置选项
 * @param {Object} options.customSceneMap - 自定义 scene 映射（可选）
 * @param {Object} options.defaultRouteInfo - 默认重定向路由信息（可选，默认 { path: '/', name: '首页' }）
 * @param {boolean} options.enableCountdown - 是否启用倒计时（可选，默认 true）
 * @param {boolean} options.refreshData - 是否在重定向前刷新数据（可选，默认 false
 * @param {number} options.countdownDuration - 倒计时时长（秒，可选，默认从 query.countdown 或 10）
 * @param {boolean} options.autoRedirect - 倒计时结束后是否自动跳转（可选，默认 true）
 * @param {Function} options.onRedirect - 跳转前的回调函数（可选）
 * @returns {Object} - Hook 返回值
 */
export function usePaymentRedirect(options = {}) {
  const {
    customSceneMap = {},
    defaultRouteInfo = {
      path: '/',
      name: t('nav.home'),
    },
    enableCountdown = true,
    countdownDuration = 10,
    autoRedirect = true,
    onRedirect = () => {},
  } = options

  const router = useRouter()
  const route = useRoute()

  const redirectRouteInfo = computed(() => {
    if (route.query.scene) {
      return getRedirectPathByScene(route.query.scene, customSceneMap)
    }
    return defaultRouteInfo
  })

  /**
   * 执行重定向
   */
  function redirect() {
    const redirectRoute = redirectRouteInfo.value

    // 执行跳转前回调
    if (onRedirect) {
      onRedirect(redirectRoute)
    }
    router.replace(redirectRoute.path)
  }

  // 倒计时相关
  const isCounting = ref(false)

  // 使用 vueuse useCountdown
  const {
    remaining,
    start: startCountdown,
    stop: stopCountdown,
  } = useCountdown(countdownDuration, {
    interval: 1000,
    immediate: false, // 不立即开始，由 enableCountdown 控制
    onComplete: () => {
      isCounting.value = false
      if (autoRedirect) {
        redirect()
      }
    },
  })

  // 如果启用倒计时，自动开始
  if (enableCountdown) {
    isCounting.value = true
    startCountdown(countdownDuration)
  }

  /**
   * 手动启动倒计时
   * @param {number} duration - 倒计时时长（可选，使用默认值）
   */
  function start(duration) {
    isCounting.value = true
    startCountdown(duration || countdownDuration)
  }

  /**
   * 手动停止倒计时
   */
  function stop() {
    isCounting.value = false
    stopCountdown()
  }

  return {
    // 重定向相关
    redirectPath: redirectRouteInfo.value.path,
    redirectTarget: redirectRouteInfo.value.name,
    redirect,

    // 倒计时相关
    remaining: enableCountdown ? remaining : ref(0),
    isCounting: computed(() => isCounting.value),
    startCountdown: start,
    stopCountdown: stop,

    // 工具函数
    getRedirectPathByScene: (scene) => getRedirectPathByScene(scene, customSceneMap),
  }
}
